import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

import { Button, CheckboxGroup, ProgressBar } from '../../components/_shared';
import AccountLayout from '../../components/layout/Account';
import Member from '../Member';
import { DashboardContainer, MenuContainer } from './styles';
import { getStudents } from '../../service/management';
import { makeSelectUser } from '../../store/selectors';
import { AppContext } from '../../providers/AppProvider';
import { notify } from '../../utils/helper';

const Grid = forwardRef(({ columnDefs, rowData }, ref) => {
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      flex: 1,
    };
  }, []);

  return (
    <AgGridReact
      ref={ref}
      rowData={rowData}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
    ></AgGridReact>
  );
});

function Manage() {
  const history = useHistory();
  const [selectedMember, setSelectedMember] = useState(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [groupFilter, setGroupFilter] = useState([]);
  const [query, setQuery] = useState('');
  const user = useSelector(makeSelectUser());
  const [data, setData] = useState([]);
  const { coreCurriculum } = useContext(AppContext);
  const gridRef = useRef();

  const [members, setMembers] = useState([]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await getStudents(user._id);
        const resData = res.data.data;

        const uniqueUsers = resData.reduce((acc, item) => {
          const existingUser = acc.find((user) => user.email === item.email);

          if (existingUser) {
            existingUser.group += ` | ${item.group}`;
            if (Array.isArray(existingUser.expireDates)) {
              existingUser.expireDates.push(...(item.expireDates || []));
            } else {
              existingUser.expireDates = item.expireDates || [];
            }
          } else {
            acc.push({
              ...item,
              expireDates: Array.isArray(item.expireDates)
                ? item.expireDates
                : [],
            });
          }

          return acc;
        }, []);

        setData(uniqueUsers);
      } catch (err) {
        notify('error', 'Failed to load students: ' + (err && err.toString()));
        setData([]);
      }
    };

    if (user) {
      fetchStudents();
    }
  }, [user]);

  useEffect(() => {
    const membersList = data.filter((member) => member.role !== 'manager');
    setMembers(membersList);
  }, [data]);

  const getPercent = useCallback(
    (progress) => {
      if (coreCurriculum) {
        const { courses } = coreCurriculum;
        const percent = courses.reduce((p, c) => {
          const userCourse = progress.find(
            ({ course: coourse }) => coourse && coourse._id === c._id
          );

          if (userCourse) {
            const total =
              userCourse.course.lessons.length +
              userCourse.course.quizzes.length;
            const current =
              userCourse.completedLessons.length +
              userCourse.completedQuizzes.length;
            return p + Math.min(100, (current / total) * 100);
          } else {
            return p;
          }
        }, 0);

        return Math.floor(parseFloat(percent / courses.length));
      }

      return 0;
    },
    [coreCurriculum]
  );

  const filteredMembers = members.filter(
    ({ name, group }) =>
      name.toLowerCase().includes(query.toLowerCase()) &&
      (groupFilter.length === 0 ||
        group.split(' | ').some((g) => groupFilter.includes(g)))
  );

  const groups = useMemo(() => {
    let arr = [];
    data.forEach((item) => {
      const gs = item.group.split(' | ');
      arr = arr.concat(gs);
    });

    return [...new Set(arr)];
  }, [data]);

  const handleSelectMember = (id) => () => {
    if (id === selectedMember) {
      setSelectedMember(null);
    } else {
      setSelectedMember(id);
    }
  };

  const handleToggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  const handleApplyFilter = (v) => {
    setGroupFilter(v);
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const coursePercent = useCallback(
    (progress, course) => {
      const userCourse = progress.find(
        ({ course: coourse }) => coourse && coourse._id === course._id
      );

      if (userCourse) {
        const total =
          userCourse.course.lessons.length + userCourse.course.quizzes.length;
        const current =
          userCourse.completedLessons.length +
          userCourse.completedQuizzes.length;
        return Math.min(100, (current / total) * 100);
      } else {
        return 0;
      }
    },
    [coreCurriculum]
  );

  const gridColumnDefs = useMemo(() => {
    if (!coreCurriculum || coreCurriculum.courses.length === 0) return [];

    const sortedCourses = [...coreCurriculum.courses].sort(
      (a, b) => a.no - b.no
    );

    return [
      {
        headerName: 'ACCELERATOR PROGRESS REPORT',
        children: [
          { field: 'no', maxWidth: 25 },
          { field: 'name', maxWidth: 150 },
          { field: 'email', minWidth: 250 },
          { field: 'group' },
        ],
      },
      {
        headerName: 'PROGRAM PROGRESS',
        children: sortedCourses.map((course) => ({
          headerName: course.title,
          field: `course_${course.no}`,
          maxWidth: 100,
        })),
      },
    ];
  }, [coreCurriculum]);

  const gridRowData = useMemo(() => {
    if (!coreCurriculum || coreCurriculum.courses.length === 0) {
      return [];
    }

    const sortedCourses = [...coreCurriculum.courses].sort(
      (a, b) => a.no - b.no
    );

    return data.map((item, index) => {
      const courseProgress = sortedCourses.reduce((acc, course) => {
        const progress = coursePercent(item.progress, course);
        acc[`course_${course.no}`] = `${Math.ceil(progress)}%`;
        return acc;
      }, {});

      return {
        no: index + 1,
        name: item.name,
        email: item.email,
        group: item.group,
        ...courseProgress,
      };
    });
  }, [coreCurriculum, data, coursePercent]);

  const handleDownloadReport = () => {
    gridRef.current.api.exportDataAsExcel({
      author: 'ACRE',
      fontSize: 14,
      headerRowHeight: 30,
      fileName: `Report_${moment(new Date()).format('YYYY/MM/DD')}`,
      sheetName: moment(new Date()).format('YYYY-MM-DD'),
    });
  };

  return (
    <AccountLayout>
      <div className="text-lg font-semibold uppercase text-black tracking-widest mb-5">
        Enterprise Dashboard
      </div>
      <DashboardContainer>
        <div className="body pt-8">
          <div className="hidden">
            <Grid
              ref={gridRef}
              columnDefs={gridColumnDefs}
              rowData={gridRowData}
            />
          </div>
          <div className="filter-bar relative justify-between w-full">
            <div className="flex items-center relative">
              <div className="search-bar flex items-center">
                <svg
                  width="20"
                  height="19"
                  viewBox="0 0 20 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.3103 15.2263L16.3286 15.2445L16.3486 15.2606L18.7756 17.2197L18.8682 17.2944C19.0705 17.5953 19.0399 18.0097 18.7763 18.2761C18.4842 18.5714 18.0148 18.5746 17.7189 18.2858L15.7164 15.9908L15.7164 15.9908L15.7139 15.9879L15.6484 15.914C15.5802 15.8236 15.5422 15.7119 15.5422 15.5956C15.5422 15.4556 15.5972 15.3225 15.6933 15.2251C15.8656 15.0553 16.1385 15.0557 16.3103 15.2263ZM8.64876 0.5C10.6756 0.5 12.6205 1.31377 14.0554 2.76405C15.4904 4.21451 16.2975 6.18288 16.2975 8.23643C16.2975 12.5142 12.868 15.9729 8.64876 15.9729C4.4295 15.9729 1 12.5142 1 8.23643C1 3.95866 4.4295 0.5 8.64876 0.5Z"
                    stroke="#62788C"
                    strokeOpacity="0.4"
                  />
                </svg>
                <input
                  placeholder="Search by name"
                  value={query}
                  onChange={handleQueryChange}
                />
              </div>
              <div
                className={cx(
                  'filter-button',
                  (showFilterMenu || groupFilter.length) && 'selected'
                )}
                onClick={handleToggleFilterMenu}
              >
                <svg
                  width="20"
                  height="17"
                  viewBox="0 0 20 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8366 11.0375C17.3855 11.0375 18.625 12.2685 18.625 13.7693C18.625 15.2689 17.3856 16.5 15.8366 16.5C14.2885 16.5 13.0482 15.2688 13.0482 13.7693C13.0482 12.2686 14.2886 11.0375 15.8366 11.0375ZM7.72964 12.891C8.25769 12.891 8.67145 13.3102 8.67145 13.8076C8.67145 14.3042 8.25744 14.7241 7.72964 14.7241H1.4418C0.914004 14.7241 0.5 14.3042 0.5 13.8076C0.5 13.3102 0.913762 12.891 1.4418 12.891H7.72964ZM3.28842 0.5C4.83741 0.5 6.07684 1.73113 6.07684 3.23075C6.07684 4.73151 4.83731 5.96254 3.28842 5.96254C1.74049 5.96254 0.5 4.73141 0.5 3.23075C0.5 1.73124 1.74038 0.5 3.28842 0.5ZM17.6843 2.31527C18.2112 2.31527 18.625 2.73439 18.625 3.23075C18.625 3.72839 18.211 4.14727 17.6843 4.14727H11.3964C10.8684 4.14727 10.4546 3.72815 10.4546 3.23075C10.4546 2.73462 10.8681 2.31527 11.3964 2.31527H17.6843Z"
                    stroke="#62788C"
                    strokeOpacity="0.4"
                  />
                </svg>
              </div>
              {showFilterMenu && (
                <OutsideClickHandler onOutsideClick={handleToggleFilterMenu}>
                  <MenuContainer>
                    <CheckboxGroup
                      value={groupFilter}
                      options={groups.map((g) => ({ key: g, label: g }))}
                      onChange={handleApplyFilter}
                    />
                  </MenuContainer>
                </OutsideClickHandler>
              )}
            </div>
            <Button caption="Download Report" onClick={handleDownloadReport} />
          </div>
          <div className="table-container ">
            <div className="table-header flex items-center ">
              <div className="">Name</div>
              <div className="">Email</div>
              <div className="">Group</div>
              <div className="flex-grow">Core Curriculum Progress</div>
              <div className="p-2">Details</div>
            </div>
            <div className="table-body">
              <div className="text-lg font-semibold uppercase text-black tracking-widest mb-2 members-header border-b border-gray-300 my-4 shadow-sm bg-white">
                Members
              </div>
              {filteredMembers.map((item) => (
                <React.Fragment key={item.id + item.group}>
                  <div className="row">
                    <div className="self-center">{item.name}</div>
                    <div className="self-center">{item.email}</div>
                    <div className="self-center">{item.group}</div>
                    <div className="flex-grow self-center">
                      <ProgressBar
                        value={getPercent(item.progress)}
                        height={4}
                      />
                    </div>
                    <div className="self-center">
                      <div
                        className="show-course-btn"
                        onClick={handleSelectMember(item.id)}
                      >
                        {selectedMember === item.id ? 'Hide' : 'Show'} Detail
                      </div>
                    </div>
                  </div>
                  {selectedMember === item.id && <Member memberId={item.id} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </DashboardContainer>
    </AccountLayout>
  );
}

export default Manage;
